import { APP_INITIALIZER, ErrorHandler, Injector, NgModule, isDevMode } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule, UrlSerializer } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { IteroEventBus } from '@itero/itero-client-communication';
import { AppRoutes } from './app.routes';
import { CoreModule } from './core/core.module';
import { ErrorsModule } from './errors/errors.module';
import { NotificationComponent } from './shared/notification/notification.component';
import { NotificationService } from './shared/notification/notification.service';
import { ModalModule } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { LowerCaseUrlSerializerService } from './core/lowerCaseUrlSerializer.service';
import { NanobarService } from './core/nanobar.service';
import { DownloadNotificationService } from './shared/downloadNotification/downloadNotification.service';
import { DownloadFileService } from './shared/downloadFile.service';
import { SoftwareOptionsService } from './core/companySoftwareOptions/service/softwareOptions.service';
import { RxService } from './rx/services/rx.service';
import { ModalIframeService } from './services/ModalIframe/modalIframe.service';
import { LabRxExtensionService } from './rx/labRxExtension/labRxExtension.service';
import { OrdersService } from './doctors/orders/orders.service';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EupToastService } from './core/toastr/eupToast.service';
import { S3ProxyPatchService } from './core/s3-proxy-patch.service';
import { FeaturesToggleSettingsService } from './featuresToggleSettings/service/featuresToggleSettings.service';
import { FeaturesToggleSettingsGuard } from './featuresToggleSettings/featuresToggleSettingsGuard.service';
import { CacheManagementService } from './cacheManagement/service/cacheManagement.service';
import { CacheManagementGuard } from './cacheManagement/cacheManagementGuard.service';
import { ErrorsDemoGuard } from './errors/errorsDemo/errorsDemoGuard.service';
import { AppConfigService } from './services/appConfig/appConfigService';
import { FeatureAvailabilityService } from './services/featureAvailability/featureAvailability.service';
import { RealTimeUpdateInitializerService } from './services/real-time-notifacations/real-time-update-initializer/real-time-update-initializer.service';
import { ErrorHandlerService } from './errors/errorHandler.service';
import { RecoverPasswordService } from './recoverPassword/services/recoverPassword.service';
import { ScreenshotToastComponent } from './core/screenshotToast/screenshotToast.component';
import { RxAppSdkService } from './rx/services/rx-app-sdk.service';
import { RxAppService } from './rx/services/rx-app.service';
import { GooglePlaceModule } from '@gotruster/ngx-google-places-autocomplete';
import { FeatureToggleStore } from './featuresToggleSettings/state/feature-toggle-store';
import { FeatureToggleQuery } from './featuresToggleSettings/state/feature-toggle-query';
import { isFeatureToggleActive } from './featuresToggleSettings/feature-toggle-storage';
// REMOVED: Old auth interceptor import
import { ShellContextService } from './services/shell-context/shell-context.service';
import { ShellQuery } from '@shared/store/shell.query';
import { ShellStore } from '@shared/store/shell.store';
import { ShellCommunicationService } from './services/shell-communication/shell-communication.service';
import { ShellErrorEvent } from './services/shell-communication/events/shell-error-event';
import { ShellGetContextEvent } from './services/shell-communication/events/shell-get-context-event';
import { ShellTokenExpiredEvent } from './services/shell-communication/events/shell-token-expired-event';
import { ShellLanguageChangedEvent } from "./services/shell-communication/events/shell-language-changed-event";
import { ShellCompanyChangedEvent } from "./services/shell-communication/events/shell-company-changed-event";
import { PlatformCommunicationService } from './platform/services/platform-communication/platform-communication.service';
import { GetAppsConfigEvent } from "@shared/communicationEvents/platformEvents/get-apps-config-event";
import { CommunicationService } from './services/client-communication/communication.service';
import { LoggingInitializerService } from '@logging/logging-initializer.service';
import { 
	provideAuthAngularInterceptor, 
	provideAuthAngularServices, 
	provideCookieAuthService,
	AUTH_ANGULAR__LOGOUT_SERVICE,
	AUTH_ANGULAR__APP_ID,
	AUTH_ANGULAR__INTERCEPT_CUSTOM_CONFIG,
	IAuthInfo
} from '@itero/auth-angular';
import { AuthService } from './services/authentication/auth.service';
import { AuthInterceptorService } from './services/interceptor/auth.interceptor.service';
import { DoctorBffRoutingInterceptor } from './services/interceptor/doctor-bff-routing.interceptor';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AppUpdater } from '@shared/appUpdater.service';
import { InternalDebugComponent } from './debug/internal-debug.component';
import { GenericUIModule } from '@shared/genericUI/module/generic-ui/generic-ui.module';
import { AdaChatBotDirective } from '@shared/ada-chat-bot/ada-chat-bot.directive';
import { AppTelemetryService } from './services/appTelemetry/apptelemetry.service';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { EupRoutesService } from "@core/eupRoutes.service";
import { PlatformLoginService } from "./services/platform-login/platform-login.service";
import { TimberService } from '@logging/timber.service';
import { firstValueFrom, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { FeatureToggleSettings } from "./featuresToggleSettings/featuresToggleSettings.model";
import { FeatureToggle } from "@shared/enums";
import { Consts } from "@shared/consts";

export function initPlatformLoginService(
	appConfigService: AppConfigService,
	eupRoutesService: EupRoutesService,
	featuresToggleSettingsService: FeaturesToggleSettingsService,
	platformLoginService: PlatformLoginService,
	timberService: TimberService
): () => Promise<any> {
	return async () => {
		try {
			await appConfigService.loadAppConfig();
			await firstValueFrom(
				eupRoutesService.doAfterInit(() => of({}), true).pipe(
					switchMap(() => featuresToggleSettingsService.getFeaturesToggleSettings()),
					tap((res: FeatureToggleSettings[]) => {
						const useIteroPlatformLogin = res.find(
							(f) => f.id === FeatureToggle.UseIteroPlatformLogin
						)?.isActive === true;
						if (useIteroPlatformLogin) {
							platformLoginService.initialize();
						}
					}),
					switchMap(() =>
						isFeatureToggleActive(FeatureToggle.UseSecuredRoutingDataEndpoint)
							? eupRoutesService.updateRoutes(true, 'AppModule')
							: of(null)
					),
					// Split-endpoint migration: on a warm reload where a session already exists,
					// the post-login flow does not run, so fetch the secured routing data here so
					// authenticated screens have their URLs. No-op when the flag is off (getSecuredRoutes
					// short-circuits) and safe otherwise: a 401/403 is handled gracefully inside it.
					switchMap(() => {
						const hasSession = !!localStorage.getItem(Consts.Storage.SessionInfo);
						return hasSession ? eupRoutesService.getSecuredRoutes() : of(null);
					})
				),
				{ defaultValue: null }
			);
		} catch (err) {
			console.error('initPlatformLoginService failed');
			timberService.error('initPlatformLoginService failed', { module: 'AppModule', error: err as Error });
		}
	};
}

@NgModule({
	imports: [
		BrowserModule,
		RouterModule,
		CoreModule,
		ErrorsModule,
		AppRoutes,
		ModalModule.forRoot(),
		BsDropdownModule.forRoot(),
		ProgressbarModule.forRoot(),
		BsDatepickerModule.forRoot(),
		TooltipModule.forRoot(),
		TranslateModule,
		CommonModule,
		BrowserAnimationsModule,
		InternalDebugComponent,
		GooglePlaceModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled: !isDevMode(),
			// Register the ServiceWorker as soon as the application is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: 'registerWhenStable:30000'
		}),
		GenericUIModule
	],
	declarations: [
		AppComponent,
		NotificationComponent,
		ScreenshotToastComponent,
		AdaChatBotDirective
	],
	providers: [
		{ provide: IteroEventBus, useValue: new IteroEventBus() },
		{
			provide: APP_INITIALIZER,
			useFactory: initPlatformLoginService,
			deps: [AppConfigService, EupRoutesService, FeaturesToggleSettingsService, PlatformLoginService, TimberService],
			multi: true
		},
		NotificationService,
		LabRxExtensionService,
		RxService,
		RxAppSdkService,
		ModalIframeService,
		DownloadNotificationService,
		DownloadFileService,
		SoftwareOptionsService,
		RecoverPasswordService,
		FeaturesToggleSettingsService,
		FeatureToggleStore,
		FeatureToggleQuery,
		FeaturesToggleSettingsGuard,
		CacheManagementService,
		CacheManagementGuard,
		ErrorsDemoGuard,
		EupToastService,
		RxAppService,
		
		// Enable interceptors from DI
		provideHttpClient(withInterceptorsFromDi()),
		
		// 🔧 HYBRID AUTH SETUP - npm package handles token refresh, custom handles headers
		provideAuthAngularInterceptor,     // ENABLED - handles token refresh automatically
		provideAuthAngularServices,        // ENABLED - needed by other services
		provideCookieAuthService,          // ENABLED - needed by AuthCookiesService
		
		// Custom interceptor for additional headers only
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptorService,
			multi: true
		},
		// Interceptor to route Doctor BFF APIs to WD URL
		{
			provide: HTTP_INTERCEPTORS,
			useClass: DoctorBffRoutingInterceptor,
			multi: true
		},
		
		// Configure the auth library
		{
			provide: AUTH_ANGULAR__APP_ID,
			useValue: 'midc'
		},
		{
			provide: AUTH_ANGULAR__INTERCEPT_CUSTOM_CONFIG,
			useValue: {
				tokenHeaderName: 'Authorization',
				prefixToken: 'Bearer ',
				countOfRefreshRetries: 3,
				refreshPath: 'api/v1/auth/session/refresh'
			}
		},
		{
			provide: AUTH_ANGULAR__LOGOUT_SERVICE,
			useValue: {
				logout(authInfo: IAuthInfo) {
					// Dispatch event to be handled by app.component.ts
					const event = new CustomEvent('midc_event_onTokenExpired', {
						detail: {
							appId: 'midc',
							sessionId: authInfo.sessionId,
							payload: { returnUrl: window.location.pathname + window.location.search }
						}
					});
					window.dispatchEvent(event);
				}
			}
		},
		
		ShellStore,
		ShellQuery,
		ShellContextService,
		ShellCommunicationService,
		{
			provide: UrlSerializer,
			useClass: LowerCaseUrlSerializerService
		},
		OrdersService,
		NanobarService,
		FeatureAvailabilityService,
		{
			provide: ErrorHandler,
			useClass: ErrorHandlerService
		},
		// S3 Proxy Patch - Initialize early to intercept micro-frontend requests
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [S3ProxyPatchService],
			useFactory: (s3ProxyPatchService: S3ProxyPatchService) => {
				return () => {
					s3ProxyPatchService.applyPatch();
				};
			}
		},
		// AUTH: Restore auth state from localStorage BEFORE app starts
		// This ensures Bearer tokens are available for HTTP requests made during initialization
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [AuthService],
			useFactory: (authService: AuthService) => {
				return () => {
					authService.restoreAuthFromStorage();
				};
			}
		},
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [AppConfigService, Injector, AppTelemetryService],
			useFactory: (appConfigService: AppConfigService, injector: Injector) => {
				return () => {
					return appConfigService
						.loadAppConfig()
						.then(() => {
							injector.get(RealTimeUpdateInitializerService).trackAuthentication();
						})
						.catch(console.error);
				};
			}
		},
		// Initialize logging immediately after appConfig is loaded, before any other code runs
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [LoggingInitializerService],
			useFactory: (loggingInitializerService: LoggingInitializerService) => {
				return () => loggingInitializerService.initialize();
			}
		},
		// REMOVED: Old HTTP_INTERCEPTORS provider - now handled by provideAuthAngularInterceptor
		ShellErrorEvent,
		ShellGetContextEvent,
		ShellTokenExpiredEvent,
		ShellLanguageChangedEvent,
		ShellCompanyChangedEvent,
		PlatformCommunicationService,
		GetAppsConfigEvent,
		CommunicationService,
		LoggingInitializerService,
		AppUpdater
	],
	bootstrap: [AppComponent]
})
export class AppModule { }

